import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Package, ChevronLeft, ChevronRight } from 'lucide-react';

const HeroSection = () => {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);

  const bannerImages = [
    { id: 1, alt: 'T.C.M. Detergent Powder Range', url: 'https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/rh63mgph_1%20rotating%20carasoul.png' },
    { id: 2, alt: 'T.C.M. Detergent Soap', url: 'https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/hx5p8i1n_2%20rotating%20carasou%3B.png' },
    { id: 3, alt: 'T.C.M. and Jabaj Products', url: 'https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/0ynxvbfe_3%20rotating%20caraousl.png' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % bannerImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [bannerImages.length]);

  const productOverview = [
    { key: 'tcmPowder', icon: Package, image: 'https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/xjznzyks_4%20T.C.M.%C2%AE%20Detergent%20Powder.png' },
    { key: 'tcmSoapOnly', icon: Package, image: 'https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/uxo8yfz3_5%20T.C.M.%C2%AE%20Detergent%20Soap.png' },
    { key: 'jabajSoapOnly', icon: Package, image: 'https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/ctnc380v_6%20Jabaj%C2%AE%20Detergent%20Soap.png' },
    { key: 'bartanBar', icon: Package, image: 'https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/g8dlzj3f_file_000000007a187208875cc36ed46623f7.png' },
    { key: 'oilBasedSoap', icon: Package, image: 'https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/ehc9irz8_7%20Oil%20Based%20Laundry%20Soap%20-%20Copy.png' },
    { key: 'liquidProducts', icon: Package, image: 'https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/4lcb42lp_8%20Liquid%20Cleaning%20Products.png' },
  ];

  return (
    <section id="home" className="pt-20" data-testid="hero-section">
      {/* Banner Carousel */}
      <div className="relative w-full h-[210px] md:h-[290px] lg:h-[370px] bg-gradient-to-r from-blue-50 to-cyan-50 overflow-hidden" data-testid="banner-carousel">
        {bannerImages.map((img, index) => (
          <div
            key={img.id}
            className={`banner-slide ${index === currentSlide ? 'active' : ''} flex items-center justify-center`}
            data-testid={`banner-slide-${index}`}
          >
            <img 
              src={img.url} 
              alt={img.alt}
              className="h-full object-contain"
              style={{ width: '160%', maxWidth: '160%' }}
            />
          </div>
        ))}
        
        {/* Carousel Controls */}
        <button 
          onClick={() => setCurrentSlide((prev) => (prev - 1 + bannerImages.length) % bannerImages.length)}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white flex items-center justify-center shadow-md transition-all"
          data-testid="banner-prev"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button 
          onClick={() => setCurrentSlide((prev) => (prev + 1) % bannerImages.length)}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white flex items-center justify-center shadow-md transition-all"
          data-testid="banner-next"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Carousel Dots */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {bannerImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide ? 'bg-[#0047AB] w-8' : 'bg-white/60'
              }`}
              data-testid={`banner-dot-${index}`}
            />
          ))}
        </div>
      </div>

      {/* Hero Content */}
      <div className="container-custom section-spacing">
        <h1 
          className="text-3xl md:text-5xl lg:text-6xl font-black text-[#0047AB] uppercase tracking-tight text-center mb-8"
          data-testid="hero-title"
        >
          {t('heroTitle')}
        </h1>
        <div className="section-divider mb-8"></div>
        <p 
          className="text-base md:text-lg text-slate-600 leading-relaxed max-w-4xl mx-auto text-center mb-16"
          data-testid="hero-intro"
        >
          {t('heroIntro')}
        </p>

        {/* Product Overview Blocks */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6" data-testid="product-overview">
          {productOverview.map((product, index) => (
            <div 
              key={index}
              className="card-product p-4 text-center product-card"
              data-testid={`product-overview-${index}`}
            >
              <div className="aspect-square bg-slate-50 mb-3 flex items-center justify-center overflow-hidden">
                <img 
                  src={product.image} 
                  alt={t(product.key)}
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="font-bold text-slate-800 text-sm md:text-base">{t(product.key)}</h3>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
